import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@tsin-env/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const BASE = `${environment.apiUrl2}/screener-queue`;
const QC_BASE = `${environment.apiUrl2}/qc-workflow`;
const USER_BASE = `${environment.apiUrl2}/user`;
const APPLICATIONS_BASE = `${environment.apiUrl2}/applications`;

export interface ScreenerQueueFilter {
  cohortId?: string;
  drawId?: string;
  assignedToAdminId?: string;
  status?: string;
  phase?: 1 | 2;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface AssignScreenerPayload {
  adminId: string;
  adminName: string;
  noteEn?: string;
  noteFr?: string;
}

export interface UnassignScreenerPayload {
  noteEn?: string;
  noteFr?: string;
}

export interface UpdateQueueStatusPayload {
  status: string;
}

export interface AutoDistributePayload {
  /** Activated PortalDraw ID to distribute (preferred). / Tirage activé à distribuer. */
  drawId?: string;
  /** Cohort (exam) ID fallback when drawId omitted. / Cohorte de repli. */
  cohortId?: string;
  screenerIds?: string[];
}

/** Activate applications from a PortalDraw into the screening queue.
 *  Activer les candidatures d'un tirage dans la file d'attente. */
export interface PullFromDrawPayload {
  drawId: string;
}

/** Submit a preliminary screening decision with referral routing.
 *  Soumettre une décision préliminaire avec acheminement de référence. */
export interface SubmitScreeningDecisionPayload {
  decision: string;
  commentEn?: string;
  commentFr?: string;
  flags?: string[];
  /** 'qc' = standard QC | 'manager' = manager sign-off | 'cd' = direct CD escalation */
  referralTarget: 'qc' | 'manager' | 'cd';
  referralReasonEn?: string;
  referralReasonFr?: string;
}

export interface QCFinding {
  step: string;
  stepLabel: string;
  stepLabelFr: string;
  result: 'Confirmed' | 'Flag' | 'N/A';
  note?: string;
  noteFr?: string;
}

export interface SubmitQCReviewPayload {
  findings: QCFinding[];
  overallResult: 'Pass' | 'Pass with Notes' | 'Escalate to CD';
  phase: 1 | 2;
  escalationReason?: string;
  escalationReasonFr?: string;
}

export interface UpdateBriefDecisionPayload {
  recommendationStatus: string;
  manualBriefText?: string;
  manualBriefTextFr?: string;
}

/** B4 — Re-entry brief returned by the backend.
 *  Résumé de reprise retourné par le serveur. */
export interface ReentryBriefResult {
  generatedBy: 'system-ai' | 'system-rules';
  summaryEn: string;
  summaryFr: string;
  confidence: 'high' | 'medium' | 'low';
  sessionCount: number;
  daySpan: number;
  isHandoff: boolean;
  generatedAt?: string;
}

/** KYE-E02b — Run lifecycle outcomes recorded against every inbound webhook.
 *  Mirrors `KyeRunStatusEnum` on the server.
 *  KYE-E02b — Résultats de cycle de vie des exécutions Kye (serveur). */
export type KyeRunStatus = 'accepted' | 'dedup' | 'rejected_classification' | 'rejected_schema' | 'requested' | 'failed';

/** KYE-E02b — Aggregated Kye KPIs returned in the `kye` block of the
 *  screener-queue metrics endpoint. All values are undefined when
 *  `enabled === false` (feature flag off or computation degraded).
 *  KYE-E02b — KPI Kye agrégés retournés dans le bloc `kye`. */
export interface KyeKpiResult {
  enabled: boolean;
  window?: { fromIso: string; toIso: string; days: number };
  runs?: {
    total: number;
    byStatus: Record<KyeRunStatus, number>;
  };
  noteActions?: {
    accepted: number;
    dismissed: number;
    edited: number;
    copied: number;
    acceptRatio: number;
    dismissRatio: number;
    copyRatio: number;
  };
  dwellTimeMs?: { medianMs: number | null; p95Ms: number | null };
  /** Fraction (0-1) of applications whose latest Kye output is stale relative
   *  to the application document. Current-state signal, not window-bound.
   *  Fraction (0-1) des candidatures dont la sortie Kye est obsolète. */
  stalenessRate?: number;
}

/** KYE-E02b — Full response shape of `GET /screener-queue/metrics`. The
 *  pre-existing queue-health fields are unchanged; `kye` is additive.
 *  Forme complète de la réponse de l'endpoint de métriques. */
export interface ScreenerQueueMetricsResult {
  queueDepth: number;
  cdDepth: number;
  escalationRate: number;
  totalWithQCStatus: number;
  alerts: string[];
  alertsTriggered: boolean;
  thresholds: { cdDepth: number; escalationPct: number };
  generatedAt: string;
  kye: KyeKpiResult;
}

/** PRO Screener Queue & QC Workflow HTTP service.
 *  Service HTTP pour la file d'attente des examinateurs et le flux AQ PRO. */
@Injectable({ providedIn: 'root' })
export class ScreenerQueueHttpService {
  private readonly http = inject(HttpClient);

  // ─── Screener Queue ──────────────────────────────────────────────────────

  getQueue(filter: ScreenerQueueFilter = {}): Observable<{ data: any[]; total: number; page: number; limit: number }> {
    let params = new HttpParams();
    Object.entries(filter).forEach(([k, v]) => {
      if (v !== undefined && v !== null) params = params.set(k, String(v));
    });
    return this.http.get<any>(BASE, { params });
  }

  getApplication(id: string): Observable<any> {
    return this.http.get<any>(`${BASE}/${id}`);
  }

  getStats(cohortId?: string): Observable<any[]> {
    let params = new HttpParams();
    if (cohortId) params = params.set('cohortId', cohortId);
    return this.http.get<any[]>(`${BASE}/stats`, { params });
  }

  /** KYE-E02 — Observability metrics including the additive `kye` KPI block.
   *  Backend RBAC restricts this to admin/QC/CD reviewer roles; callers must
   *  gate the call client-side as well to avoid noisy 403s.
   *  Métriques d'observabilité incluant le bloc additif `kye`. */
  getMetrics(cohortId?: string): Observable<ScreenerQueueMetricsResult> {
    let params = new HttpParams();
    if (cohortId) params = params.set('cohortId', cohortId);
    return this.http.get<ScreenerQueueMetricsResult>(`${BASE}/metrics`, { params });
  }

  /** Fetch all users with the proScreener role for the assignment dropdown.
   *  Récupérer tous les utilisateurs avec le rôle proScreener pour la liste déroulante. */
  getScreeners(): Observable<{ _id: string; firstName: string; lastName: string; email: string }[]> {
    return this.http.get<{ data: { _id: string; firstName: string; lastName: string; email: string }[] }>(USER_BASE, { params: { role: 'proScreener' } }).pipe(map((res) => res.data ?? []));
  }

  assignScreener(id: string, payload: AssignScreenerPayload): Observable<any> {
    return this.http.post<any>(`${BASE}/${id}/assign`, payload);
  }

  unassignScreener(id: string, payload: UnassignScreenerPayload = {}): Observable<any> {
    return this.http.post<any>(`${BASE}/${id}/unassign`, payload);
  }

  updateQueueStatus(id: string, payload: UpdateQueueStatusPayload): Observable<any> {
    return this.http.patch<any>(`${BASE}/${id}/status`, payload);
  }

  autoDistribute(payload: AutoDistributePayload): Observable<{ assigned: number; total: number }> {
    return this.http.post<any>(`${BASE}/auto-distribute`, payload);
  }

  /** Activate all applications in a PortalDraw into the screening queue (status = Pending).
   *  Activer toutes les candidatures d'un tirage dans la file d'attente (statut = Pending). */
  pullFromDraw(payload: PullFromDrawPayload): Observable<{ drawName: string; activated: number; message: string }> {
    return this.http.post<any>(`${BASE}/pull-from-draw`, payload);
  }

  /** Manually (re-)trigger KYE agents for every application in an activated draw.
   *  When force=true the dedup check is bypassed — every app gets a fresh run.
   *
   *  Déclencher manuellement (re-) les agents KYE pour toutes les candidatures
   *  d'un tirage activé. force=true contourne le dédup pour un run frais. */
  triggerKyeForDraw(drawId: string, force = false, retrospective = false): Observable<{ drawName: string; triggered: number; message: string }> {
    const params: string[] = [];
    if (force) params.push('force=true');
    if (retrospective) params.push('retrospective=true');
    const qs = params.length ? `?${params.join('&')}` : '';
    const url = `${BASE}/draws/${encodeURIComponent(drawId)}/trigger-kye${qs}`;
    return this.http.post<any>(url, {});
  }

  /** Submit a preliminary screening decision and route the application.
   *  Soumettre une décision préliminaire et acheminer la candidature. */
  submitScreeningDecision(id: string, payload: SubmitScreeningDecisionPayload): Observable<any> {
    return this.http.post<any>(`${BASE}/${id}/submit-decision`, payload);
  }

  /** B4 — Get or generate the screener re-entry brief for an application.
   *  Obtenir ou générer le résumé de reprise pour l'examinateur. */
  getReentryBrief(id: string): Observable<ReentryBriefResult> {
    return this.http.get<ReentryBriefResult>(`${BASE}/${id}/reentry-brief`);
  }

  // ─── PA Manual Link ──────────────────────────────────────────────────────

  /** Manually link a PhysiciansApply record to a single application.
   *  Admin/QC/super-admin only (RBAC enforced server-side).
   *  When `candidateCode` is omitted the backend auto-detects it from the
   *  application's personal-information step data.
   *
   *  Lier manuellement un dossier PhysiciansApply à une candidature individuelle.
   *
   * @param applicationId  ProUserApplication ObjectId (path `:id`)
   * @param candidateCode  PA candidateCode2 to search for (optional — omit to auto-detect)
   */
  linkPaRecord(applicationId: string, candidateCode?: string): Observable<{ data: { linked: boolean; candidateCode: string; applicationNo: string } }> {
    return this.http.patch<{ data: { linked: boolean; candidateCode: string; applicationNo: string } }>(`${APPLICATIONS_BASE}/${encodeURIComponent(applicationId)}/link-pa`, candidateCode ? { candidateCode } : {});
  }

  // ─── QC Workflow ─────────────────────────────────────────────────────────

  getQCQueue(filter: { cohortId?: string; phase?: number } = {}): Observable<any[]> {
    let params = new HttpParams();
    Object.entries(filter).forEach(([k, v]) => {
      if (v !== undefined) params = params.set(k, String(v));
    });
    return this.http.get<any[]>(QC_BASE, { params });
  }

  getCDReviewQueue(filter: { cohortId?: string } = {}): Observable<any[]> {
    let params = new HttpParams();
    if (filter.cohortId) params = params.set('cohortId', filter.cohortId);
    return this.http.get<any[]>(`${QC_BASE}/cd-review`, { params });
  }

  /** Load the manager review queue — applications with preliminary decisions awaiting final sign-off.
   *  Charger la file de révision du gestionnaire — candidatures avec décisions préliminaires en attente. */
  getManagerReviewQueue(filter: { cohortId?: string; phase?: number } = {}): Observable<any[]> {
    let params = new HttpParams();
    Object.entries(filter).forEach(([k, v]) => {
      if (v !== undefined) params = params.set(k, String(v));
    });
    return this.http.get<any[]>(`${QC_BASE}/manager-review`, { params });
  }

  getQCApplication(id: string): Observable<any> {
    return this.http.get<any>(`${QC_BASE}/${id}`);
  }

  initiateQCReview(id: string): Observable<any> {
    return this.http.post<any>(`${QC_BASE}/${id}/initiate`, {});
  }

  submitQCReview(id: string, payload: SubmitQCReviewPayload): Observable<any> {
    return this.http.post<any>(`${QC_BASE}/${id}/submit`, payload);
  }

  updateBriefDecision(id: string, payload: UpdateBriefDecisionPayload): Observable<any> {
    return this.http.patch<any>(`${QC_BASE}/${id}/brief-decision`, payload);
  }

  /** Phase 1D — Finalize (publish) the most recent preliminary decision as the official final decision.
   *  Phase 1D — Finaliser (publier) la dernière décision préliminaire en tant que décision officielle finale. */
  finalizeDecision(id: string, payload: { noteEn?: string; noteFr?: string } = {}): Observable<any> {
    return this.http.post<any>(`${QC_BASE}/${id}/finalize-decision`, payload);
  }

  // ─── B5: AI Document Validation / Validation IA des documents ────────────

  /** Trigger AI document validation (photo + ID) and persist results.
   *  Déclencher la validation IA des documents (photo + pièce d'identité) et persister les résultats. */
  triggerDocumentValidation(id: string): Observable<void> {
    return this.http.post<void>(`${BASE}/${id}/validate-documents`, {});
  }

  // ─── B6: Resume Template Compliance / Conformité du modèle de CV ─────────

  /** Trigger AI resume template compliance check and persist results.
   *  Déclencher la vérification de conformité du modèle de CV par IA et persister les résultats. */
  triggerResumeValidation(id: string): Observable<void> {
    return this.http.post<void>(`${BASE}/${id}/validate-resume`, {});
  }
}
